// src/components/OnboardingAgreement.jsx
//
// Onboarding agreement gate — brief v1.1, "Onboarding Agreement":
//   "Displayed before first use. User must tick checkbox to proceed.
//    Account activation blocked until confirmed."
//
// This modal collects the acceptance. It is NOT the enforcement point: the
// analysis endpoints refuse server-side while users.terms_accepted_at is
// NULL, so removing this component from the DOM restores nothing. That
// separation is what makes the gate structural rather than cosmetic, as the
// brief requires.
//
// Mount it high in the tree — inside the authenticated layout or App — so it
// covers every route rather than only the analyse page.

import { useState } from 'react'
import { acceptTerms } from '../api/services'

// Wording approved by the client August 2026. Do not paraphrase, reflow
// or summarise it.
const AGREEMENT_TEXT =
    'GMC uses artificial intelligence to analyse and rewrite communications. Its ' +
    'outputs are provided as guidance only and do not constitute legal, employment ' +
    'or other professional advice. ' +
    'You remain responsible for reviewing any output and deciding whether to send ' +
    'or rely upon it. Where a matter involves formal disciplinary action, ' +
    'termination, legal proceedings or regulatory compliance, appropriate qualified ' +
    'professional advice should be considered. ' +
    'GMC does not accept liability for decisions or outcomes arising from reliance ' +
    'on its outputs, subject to the applicable Terms of Service.'

export default function OnboardingAgreement({ onAccepted }) {
    const [checked, setChecked] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState('')

    const handleAccept = async () => {
        if (!checked || submitting) return

        setSubmitting(true)
        setError('')

        try {
            await acceptTerms()
            // Re-fetch the profile so terms_accepted flips true and the gate
            // unmounts. Awaited, so the modal stays up until the server has
            // actually confirmed rather than closing optimistically.
            await onAccepted?.()
        } catch {
            setError('We could not record your acceptance. Please try again.')
            setSubmitting(false)
        }
    }

    return (
        // No dismiss control and no backdrop click handler — the agreement
        // cannot be closed, only accepted. Deliberate: the brief blocks
        // activation until confirmed.
        <div
            className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center px-4 py-8 overflow-y-auto"
            role="dialog"
            aria-modal="true"
            aria-labelledby="onboarding-agreement-title"
        >
            <div className="w-full max-w-2xl bg-[#0D1117] border border-brand-amber/25 rounded-2xl shadow-2xl my-auto">

                {/* Header */}
                <div className="px-7 sm:px-9 pt-8 pb-6 border-b border-white/[0.06]">
                    <div className="inline-flex items-center gap-2.5 bg-brand-amber/[0.06] border border-brand-amber/25 rounded-full px-4 py-1.5 mb-5">
                        <span className="w-2 h-2 rounded-full bg-brand-amber" />
                        <span className="text-xs font-sans font-semibold tracking-widest uppercase text-brand-amber">
                            Before you begin
                        </span>
                    </div>
                    <h2
                        id="onboarding-agreement-title"
                        className="font-display text-2xl sm:text-3xl font-bold text-white tracking-tight"
                    >
                        User agreement
                    </h2>
                    <p className="text-white/50 font-body text-sm mt-2">
                        Please read and accept this before running your first analysis.
                    </p>
                </div>

                {/* Agreement text */}
                <div className="px-7 sm:px-9 py-7">
                    <div className="bg-white/[0.03] border border-white/[0.08] rounded-xl p-6">
                        <p className="text-white/80 font-body text-base leading-[1.85]">
                            {AGREEMENT_TEXT}
                        </p>
                    </div>

                    <label className="flex items-start gap-3 mt-6 cursor-pointer select-none">
                        <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => setChecked(e.target.checked)}
                            className="mt-1 w-4 h-4 shrink-0 accent-brand-amber cursor-pointer"
                        />
                        <span className="text-white/70 font-body text-sm leading-relaxed">
                            I have read and agree to the above.
                        </span>
                    </label>

                    {error && (
                        <div className="mt-5 border border-red-500/30 bg-red-500/10 rounded-xl px-4 py-3">
                            <p className="text-red-400 font-body text-sm">{error}</p>
                        </div>
                    )}
                </div>

                {/* Action */}
                <div className="px-7 sm:px-9 pb-8">
                    <button
                        type="button"
                        onClick={handleAccept}
                        disabled={!checked || submitting}
                        className="w-full px-6 py-3.5 rounded-xl text-sm font-bold tracking-wide transition-all duration-200
                            bg-brand-amber hover:bg-brand-amber2 text-[#0D1117]
                            shadow-[0_4px_20px_-4px_rgba(212,168,67,0.45)]
                            disabled:bg-white/10 disabled:text-white/30 disabled:shadow-none disabled:cursor-not-allowed"
                    >
                        {submitting ? 'Recording your acceptance…' : 'Accept and continue'}
                    </button>

                    <p className="text-white/30 font-body text-xs text-center mt-4 leading-relaxed">
                        This agreement is recorded against your account and cannot be skipped.
                    </p>
                </div>
            </div>
        </div>
    )
}